import { Card, CardBody, Table, Spinner, Badge, Row, Col, Input, Button, Label } from "reactstrap";
import React, { useState, useEffect, useRef } from "react";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import { useParams } from "react-router-dom";
import { fetchLedgerGrouped } from "../../helpers/api/finance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalculator, faChevronRight, faFilePdf, faPrint, faCheckCircle, faSearch, faUser, faBuilding, faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import Logo from "../navbar/Logo";
import Select from "react-select";
import ButtonIcon from "../common/ButtonIcon";
import SupportLink from '../root/support/components/SupportLink';
import { BolivarAmount, RateNote, CalculatedRateNote } from "./BookRate";

const Ledger = () => {
  const { id } = useParams();
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMovements, setSearchMovements] = useState("");
  const reportRef = useRef();
  const { condominium, address, phone } = useSelector((state) => state.condominium);

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchLedgerGrouped(id, startDate, endDate);
      setLedgerData(data);
    } catch (error) {
      console.error("Error loading ledger data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
  });

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    setTimeout(async () => {
      try {
        const element = reportRef.current;
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          scrollY: -window.scrollY,
          windowHeight: element.scrollHeight,
          height: element.scrollHeight,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }

        pdf.save(`Libro_Mayor_${startDate}_${endDate}.pdf`);
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        setIsExporting(false);
      }
    }, 200);
  };

  const formatNumber = (num, showZero = false) => {
    if ((num === 0 || num === null || num === undefined) && !showZero) return "-";
    if (num === 0) return "0.00";
    return new Intl.NumberFormat("es-VE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getMoveDescription = (move) => {
    try {
      const parsed = typeof move.description === 'string' && move.description.startsWith('{')
        ? JSON.parse(move.description)
        : move.description;

      return parsed?.message || (typeof parsed === 'string' ? parsed : move.description);
    } catch (e) {
      return move.description;
    }
  };

  const getMoveMetadata = (move) => {
    return (
      <div className="d-flex flex-wrap gap-2 mt-1 ml-4 whitespace-nowrap overflow-hidden">
        {move.reference && (
          <Badge color="soft-secondary" className="fs--2 py-1 px-2 rounded-pill font-weight-semi-bold border border-300">
            <FontAwesomeIcon icon={faFileInvoice} className="mr-1 fs--3" />
            Ref: {move.reference}
          </Badge>
        )}
      </div>
    );
  };

  const getConceptKey = (move, accountCode) => {
    const desc = move.description || 'Sin concepto';

    const tagMatch = desc.match(/\(([^)]+)\)/);
    const tag = tagMatch ? tagMatch[1] : null;

    if (move.property?.id || tag) {
      const entityId = move.property?.id || tag;
      return `ENTITY-PROP-${entityId}`;
    }
    if (move.provider?.id) return `ENTITY-PROV-${move.provider.id}`;

    if (accountCode && accountCode.startsWith('1.1.2.99')) {
      const date = new Date(move.date + 'T00:00:00');
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      return `PLANILLA-${period}`;
    }

    let base = desc
      .replace(/^(Provisión Gasto:|Traspaso a Planilla:|GASTO de|ExC:|CxC:|CxP:|Recaudo Admin:|Ejecución de Recaudo:|Liquidación de Gastos:|Provision Gasto:|Abono parcial correspondiente a)\s*/i, '')
      .replace(/\s*[\[\(\-]\s*(Enero|Febrero|Marzo|Abril|Mayo|Junio|Julio|Agosto|Septiembre|Octubre|Noviembre|Diciembre|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|202\d).*/i, '')
      .trim();

    return base.toLowerCase() || 'misc';
  };

  const concepts = Array.from(new Set(
    ledgerData
      .filter(acc => !searchTerm || acc.code === searchTerm)
      .flatMap(acc => acc.movements?.map(m => m.description) || [])
  )).sort();

  const conceptOptions = [
    { value: "", label: concepts.length > 0 ? "Todos los conceptos" : "Sin conceptos" },
    ...concepts.map(c => ({ value: c, label: c }))
  ];

  const filteredLedger = ledgerData
    .filter(acc => !searchTerm || acc.code === searchTerm)
    .map(acc => {
      const items = acc.movements?.filter(m =>
        !searchMovements || m.description === searchMovements
      ) || [];

      return {
        ...acc,
        filteredMovements: items,
      };
    })
    .filter(acc => acc.filteredMovements.length > 0 || (!searchMovements && acc.saldo_inicial !== 0));

  const iso = ledgerData.find(acc => acc.iso)?.iso;
  const hasCalculatedRate = filteredLedger.some(acc => acc.filteredMovements.some(m => m.rate?.calculated));

  const accountOptions = [
    { value: "", label: "Todas las cuentas" },
    ...ledgerData
      .sort((a, b) => String(a.code).localeCompare(String(b.code)))
      .map(acc => ({ value: acc.code, label: `${acc.code} - ${acc.account}` }))
  ];

  const unifiedSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '36px',
      height: '36px',
      fontSize: '0.85rem',
      borderRadius: '4px',
      border: state.isFocused ? '1px solid #b6c1d2' : '1px solid #d8e2ef',
      boxShadow: 'none',
      backgroundColor: '#fff',
      '&:hover': { borderColor: state.isFocused ? '#b6c1d2' : '#b6c1d2' }
    }),
    valueContainer: (base) => ({ ...base, height: '34px', padding: '0 12px' }),
    menu: (base) => ({
      ...base,
      fontSize: '0.85rem',
      zIndex: 9999,
      borderRadius: '8px',
      marginTop: '4px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #edf2f7'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#d8e2ef' : state.isFocused ? '#f9fafb' : 'transparent',
      color: '#4d5969',
      cursor: 'pointer',
    })
  };

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Libro Mayor"
        titleTag="h2"
        description="Resumen acumulado de movimientos agrupados por cuenta contable."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>

      <Card className="shadow-none border-0 overflow-hidden mb-3">
        <FalconCardHeader title="Libro Mayor" titleTag="h3" light={false} className="border-bottom py-2" />

        <CardBody className="bg-light border-bottom py-2">
          {/* Fila 1: Filtro de Fecha */}
          <Row className="mb-2">
            <Col xs={12}>
              <Label className="fs--2 fw-bold text-600 mb-1 text-uppercase">Rango de Fechas</Label>
              <div className="d-flex align-items-center gap-1 bg-100 p-1 rounded-2 border">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-transparent border-0 text-600 fw-bold fs--2 px-2">DESDE</span>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="form-control border-0 bg-transparent fs--1 px-1" />
                </div>
                <div className="vr text-300 d-none d-sm-block" style={{ height: '20px' }}></div>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-transparent border-0 text-600 fw-bold fs--2 px-2">HASTA</span>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="form-control border-0 bg-transparent fs--1 px-1" />
                </div>
              </div>
            </Col>
          </Row>

          {/* Fila 2: Filtros de Cuenta, Concepto y Botón Consultar */}
          <Row className="g-2 mb-2 align-items-end">
            <Col xs={12} md={5}>
              <Label className="fs--2 fw-bold text-600 mb-1 text-uppercase">Cuenta</Label>
              <Select
                options={accountOptions}
                value={accountOptions.find(opt => opt.value === searchTerm)}
                onChange={(opt) => {
                  setSearchTerm(opt.value);
                  setSearchMovements("");
                }}
                placeholder="Todas las cuentas"
                styles={unifiedSelectStyles}
                isSearchable={true}
              />
            </Col>
            <Col xs={12} md={5}>
              <Label className="fs--2 fw-bold text-600 mb-1 text-uppercase">Concepto</Label>
              <Select
                options={conceptOptions}
                value={conceptOptions.find(opt => opt.value === searchMovements)}
                onChange={(opt) => setSearchMovements(opt.value)}
                placeholder="Todos los conceptos"
                styles={unifiedSelectStyles}
                isSearchable={true}
              />
            </Col>
            <Col xs={12} md={2}>
              <Button
                color="primary"
                onClick={loadData}
                className="w-100 shadow-none fw-bold"
                style={{ height: '36px' }}
              >
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                Consultar
              </Button>
            </Col>
          </Row>

          {/* Fila 3: Botones de Exportar */}
          <div className="d-flex align-items-center gap-2 pt-1">
            <ButtonIcon
              color="falcon-default"
              size="sm"
              icon={faFilePdf}
              className="fs--1"
              onClick={handleDownloadPDF}
              disabled={loading || ledgerData.length === 0}
            >
              Descargar PDF
            </ButtonIcon>
            <ButtonIcon
              color="falcon-default"
              size="sm"
              icon={faPrint}
              className="fs--1"
              onClick={handlePrint}
              disabled={loading || ledgerData.length === 0}
            >
              Imprimir
            </ButtonIcon>
          </div>
        </CardBody>

        <div ref={reportRef} className="bg-white">
          <div className={`${!isExporting ? 'd-none' : 'd-block'} d-print-block p-4 border-bottom mb-3`}>
            <Row className="align-items-center">
              <Col xs={6}><Logo width={120} to={null} className="justify-content-start" /></Col>
              <Col xs={6} className="text-right text-end">
                <h4 className="mb-0 text-primary fw-black">LIBRO MAYOR</h4>
                <p className="mb-0 fs--1 fw-semi-bold">{condominium}</p>
                <p className="mb-0 fs--2 text-500">{address}</p>
                <p className="mb-0 fs--2 text-500">RIF: {phone}</p>
              </Col>
            </Row>
            <hr className="my-3" />
            <div className="d-flex justify-content-between">
              <span className="fs--1 text-700">Periodo: <strong>{startDate} / {endDate}</strong></span>
              <span className="fs--1 text-700">Generado el: {new Date().toLocaleDateString()}</span>
            </div>
          </div>

          <CardBody className="p-0">
            {!loading && hasCalculatedRate && <CalculatedRateNote />}
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" type="grow" size="lg" />
                <p className="mt-3 text-muted fw-semi-bold">Cargando...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 table-ledger">
                  <thead>
                    <tr className="bg-200">
                      <th className="py-2 px-4 border-0 fs--1 text-uppercase text-800" style={{ width: '180px' }}>Cuenta / Fecha</th>
                      <th className="py-2 text-center border-0 fs--1 text-uppercase text-800" style={{ width: "90px" }}>Asiento</th>
                      <th className="py-2 border-0 fs--1 text-uppercase text-800">Detalle / Concepto</th>
                      <th className="py-2 text-end border-0 fs--1 text-uppercase text-800" style={{ width: "120px" }}>Debe</th>
                      <th className="py-2 text-end border-0 fs--1 text-uppercase text-800" style={{ width: "120px" }}>Haber</th>
                      <th className="py-2 text-end border-0 fs--1 text-uppercase text-800 px-4" style={{ width: "135px" }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLedger.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <FontAwesomeIcon icon={faCalculator} size="3x" className="mb-3 text-200" />
                          <p className="text-muted fw-semi-bold">No se encontraron movimientos o cuentas para mostrar.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredLedger
                        .sort((a, b) => String(a.code).localeCompare(String(b.code)))
                        .map((account, index) => {
                          const movementsByDesc = account.filteredMovements.reduce((acc, move) => {
                            const key = getConceptKey(move, account.code);
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(move);
                            return acc;
                          }, {});

                          const sortedConcepts = Object.keys(movementsByDesc).sort((a, b) => {
                            const datesA = movementsByDesc[a].map(m => new Date(m.date));
                            const datesB = movementsByDesc[b].map(m => new Date(m.date));
                            const dateDiff = Math.min(...datesA) - Math.min(...datesB);
                            if (dateDiff !== 0) return dateDiff;
                            // Ante fecha empatada, desempata por número de asiento (el más
                            // antiguo primero) en vez de dejar el orden arbitrario, para que
                            // los números no salten sin razón aparente.
                            const asientosA = movementsByDesc[a].map(m => m.asiento_no ?? Infinity);
                            const asientosB = movementsByDesc[b].map(m => m.asiento_no ?? Infinity);
                            return Math.min(...asientosA) - Math.min(...asientosB);
                          });

                          return (
                            <React.Fragment key={index}>
                              {sortedConcepts.length > 0 ? (
                                sortedConcepts.map((conceptKey, dIdx) => {
                                  // Sort movements by date within the concept to allow cross-month settlement
                                  const movesForConcept = movementsByDesc[conceptKey].sort((a, b) => {
                                    const dateA = new Date(a.date + 'T00:00:00');
                                    const dateB = new Date(b.date + 'T00:00:00');
                                    if (dateA - dateB !== 0) return dateA - dateB;
                                    // If same day, put Debe (Charge) before Haber (Payment) to show flow
                                    return (b.debe - b.haber) - (a.debe - a.haber);
                                  });
                                  let tempBalanceAcrossConcept = 0;
                                  const groups = [];
                                  let currentGroup = [];

                                  movesForConcept.forEach((move) => {
                                    currentGroup.push(move);
                                    tempBalanceAcrossConcept += (move.debe - move.haber);
                                    if (Math.abs(tempBalanceAcrossConcept) < 0.01) {
                                      groups.push({ movements: currentGroup, settled: true });
                                      currentGroup = [];
                                      tempBalanceAcrossConcept = 0;
                                    }
                                  });
                                  if (currentGroup.length > 0) groups.push({ movements: currentGroup, settled: false });

                                  return (
                                    <React.Fragment key={dIdx}>
                                      {groups.map((group, gIdx) => {
                                        let groupRunningBalance = 0;
                                        let groupRunningBalanceBs = 0;
                                        return (
                                          <React.Fragment key={gIdx}>
                                            {group.movements.map((move, mIdx) => {
                                              groupRunningBalance += (move.debe - move.haber);
                                              groupRunningBalanceBs += ((move.debe_bs || 0) - (move.haber_bs || 0));
                                              const groupColor = group.settled ? '#00d27a' : '#2c7be5';
                                              const isFirstOfAccount = dIdx === 0 && gIdx === 0 && mIdx === 0;

                                              return (
                                                <tr key={`${dIdx}-${gIdx}-${mIdx}`} className={`hover-actions-trigger ${group.settled ? "bg-white" : "bg-soft-primary"} border-end border-300`}>
                                                  <td className="px-4 py-2 align-middle" style={{ maxWidth: '180px', borderLeft: `4px solid ${groupColor}` }}>
                                                    {isFirstOfAccount && (
                                                      <div className="mb-2">
                                                        <div className="fw-bold fs--2 font-monospace" style={{ color: groupColor }}>{account.code}</div>
                                                        <div className="text-800 fs--2 text-truncate fw-bold" title={account.account}>{account.account}</div>
                                                      </div>
                                                    )}
                                                    <div className="text-500 fw-semi-bold fs--2">
                                                      {new Date(move.date + 'T00:00:00').toLocaleDateString('es-VE')}
                                                    </div>
                                                    {iso && <RateNote rate={move.rate} multipleRates={move.multiple_rates} />}
                                                  </td>
                                                  <td className="text-center py-2 align-middle">
                                                    {move.asiento_no && (
                                                      <span className="bg-primary text-white px-2 py-0 rounded fs--2 fw-black d-inline-block">
                                                        #{move.asiento_no}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td className="py-2 fs--1">
                                                    <div className="d-flex flex-column">
                                                      <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon icon={faChevronRight} className="text-300 mr-2 fs--2" />
                                                        <span className="text-700 fw-semi-bold">{getMoveDescription(move)}</span>
                                                      </div>
                                                      {getMoveMetadata(move)}
                                                    </div>
                                                  </td>
                                                  <td className="text-right text-end py-2 fs--1 text-primary fw-semi-bold">
                                                    {formatNumber(move.debe)}
                                                    {move.debe !== 0 && <BolivarAmount iso={iso} value={move.debe_bs} />}
                                                  </td>
                                                  <td className="text-right text-end py-2 fs--1 text-danger fw-semi-bold">
                                                    {formatNumber(move.haber)}
                                                    {move.haber !== 0 && <BolivarAmount iso={iso} value={move.haber_bs} />}
                                                  </td>
                                                  <td className={`text-right text-end px-4 py-2 fs--1 fw-bold ${groupRunningBalance === 0 ? 'text-success' : 'text-dark'}`} style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                                    {groupRunningBalance === 0 ? (
                                                      <><FontAwesomeIcon icon={faCheckCircle} className="mr-1 text-success" />0,00</>
                                                    ) : (
                                                      <>{formatNumber(Math.abs(groupRunningBalance), true)}<small className="ml-1 opacity-40">{groupRunningBalance < 0 ? 'CR' : ''}</small></>
                                                    )}
                                                    {iso && Math.abs(groupRunningBalanceBs) >= 0.005 && (
                                                      <BolivarAmount iso={iso} value={Math.abs(groupRunningBalanceBs)} credit={groupRunningBalanceBs < 0} />
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                            {gIdx < groups.length - 1 && <tr key={`sp-g-${gIdx}`}><td colSpan="6" style={{ height: '8px', border: 'none' }}></td></tr>}
                                          </React.Fragment>
                                        );
                                      })}
                                      <tr key={`sp-d-${dIdx}`}><td colSpan="6" style={{ height: '12px', backgroundColor: '#f9fafd', border: 'none' }}></td></tr>
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                <tr className="bg-white border-bottom border-200">
                                  <td className="px-4 py-3 align-middle" style={{ maxWidth: '180px', borderLeft: '4px solid #ced4da' }}>
                                    <div className="mb-2">
                                      <div className="fw-bold fs--2 font-monospace text-500">{account.code}</div>
                                      <div className="text-800 fs--2 text-truncate fw-bold" title={account.account}>{account.account}</div>
                                    </div>
                                  </td>
                                  <td colSpan="4" className="py-3 fs--1 text-muted text-center italic">
                                    <em>Sin movimientos que mostrar en este periodo.</em>
                                  </td>
                                  <td className="text-right text-end px-4 py-3 fs--1 fw-bold text-dark" style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                    {formatNumber(Math.abs(account.saldo_final), true)}
                                    <small className="ml-1 opacity-40">{account.saldo_final < 0 ? 'CR' : ''}</small>
                                    {account.saldo_final_bs !== null && account.saldo_final_bs !== undefined && (
                                      <BolivarAmount iso={iso} value={Math.abs(account.saldo_final_bs)} credit={account.saldo_final_bs < 0} />
                                    )}
                                  </td>
                                </tr>
                              )}
                              <tr><td colSpan="6" className="py-4 border-0"></td></tr>
                            </React.Fragment>
                          );
                        })
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </div>
      </Card>
      <style>{`
        .table-ledger thead th { letter-spacing: 0.05em; font-weight: 700; }
        .bg-soft-primary { background-color: #f7f9ff !important; }
      `}</style>
    </div>
  );
};

export default withRedirect(Ledger);